import { useCallback } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { requestBuilder } from '../../http/RequestBuilder';

export enum AccountImportStatus {
    IMPORTED = 'imported',
    PENDING = 'pending',
    ERROR = 'error',
    UNIMPORTED = 'unimported',
}

export interface MembershipAccountStatus {
    accountId: string;
    status: AccountImportStatus;
    dateImported: Date;
    numTransactions: number;
    hasRunBillsJobAfterImport: boolean;
}

interface MembershipAccountImportStatusesResponse {
    accountStatusesAndCounts: MembershipAccountStatus[];
    successfulBillJobData: {
        lastSuccessfulBillJobRunDate: Date | null;
        hasPendingBillJob: boolean;
    };
    totalAccountsChecked: number;
}

const getMembershipAccountImportStatuses = requestBuilder.query<
    MembershipAccountImportStatusesResponse,
    { membershipId: string }
>({
    endpoint: ({ membershipId }) =>
        `/api/holistic-bills/${membershipId}/import-statuses`,
    queryKey: ({ membershipId }) =>
        `getMembershipAccountImportStatuses/${membershipId}`,
});

export const useGetMembershipAccountImportStatuses = (
    membershipId: string,
    usePolling?: boolean,
) => {
    const user = useAuthedUser();

    const { data, isLoading, isStale, refetch } =
        getMembershipAccountImportStatuses(
            { membershipId },
            {
                enabled: user?.id !== undefined,
                staleTime: 1000 * 60 * 2,
                refetchInterval: usePolling ? 10000 : false,
                refetchIntervalInBackground: usePolling,
            },
        );

    const refetchMembershipAccountImportStatuses = useCallback(() => {
        if (!isLoading && isStale) {
            void refetch();
        }
    }, [refetch, isLoading, isStale]);

    return {
        membershipAccountImportStatuses: data,
        isLoadingMembershipAccountImportStatuses: isLoading,
        refetchMembershipAccountImportStatuses,
    };
};
